import { PROJECT_STATUS, resolveSubmitStatus } from '@/utils/projectUtils';
import { db, auth } from '@/js/firebase';
import {
  doc,
  getDoc,
  updateDoc,
  runTransaction,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  orderBy
} from 'firebase/firestore';
import { logger } from './loggerService';
import { toastBridge } from './toastBridge';

class ProjectService {
  constructor() {
    this.logger = logger;
    this.toast = toastBridge;
  }

  async getProject(projectId) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      let projectSnap;
      try {
        projectSnap = await getDoc(projectRef);
      } catch (readError) {
        // Stale custom-claims token: the router guard admits the user based on the
        // Firestore users/{uid}.companies array, but the rules engine checks the
        // companyIds claim baked into the cached ID token, which lags behind the
        // Cloud Function that syncs it. Force-refresh the token and retry once —
        // mirrors the bootstrap flow in userAuthStore. A genuine deny fails again.
        if (readError?.code === 'permission-denied' && auth.currentUser) {
          this.logger.warn('ProjectService', 'Read denied — refreshing token and retrying');
          await auth.currentUser.getIdToken(true);
          projectSnap = await getDoc(projectRef);
        } else {
          throw readError;
        }
      }

      if (projectSnap.exists()) {
        return { id: projectSnap.id, ...projectSnap.data() };
      }

      throw new Error(`Project ${projectId} not found`);
    } catch (error) {
      // A deleted project reads as permission-denied, not "not found": the rules
      // reference resource.data, which is null once the doc is gone, so the rule
      // evaluates false. Past the token-refresh retry above, both that and a real
      // deny mean the same thing to the user — a stale link to something that
      // isn't there. Not an error-log-worthy fault.
      if (error?.code === 'permission-denied' || /not found$/.test(error.message || '')) {
        this.logger.warn('ProjectService', `Project ${projectId} unavailable (deleted or not permitted)`);
        const unavailable = new Error('This project is no longer available.');
        unavailable.code = 'project-unavailable';
        throw unavailable;
      }
      this.logger.error('ProjectService', `Failed to get project: ${error.message}`);
      throw error;
    }
  }

  async updateProject(projectId, updates, showToast = true, preserveBrief = true) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      
      // Handle brief preservation if needed
      let cleanedUpdates = {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      };
      
      // If preserveBrief is true and brief is not being explicitly updated
      if (preserveBrief && !Object.prototype.hasOwnProperty.call(updates, 'breif')) {
        // Get current project to preserve brief
        const projectSnap = await getDoc(projectRef);
        if (projectSnap.exists()) {
          const currentData = projectSnap.data();
          if (currentData.breif) {
            cleanedUpdates.breif = currentData.breif;
            this.logger.info('ProjectService', `Preserving existing brief for project ${projectId}`);
          }
        }
      }
      
      await updateDoc(projectRef, cleanedUpdates);
      
      this.logger.info('ProjectService', `Project ${projectId} updated successfully`);
      
      if (showToast) {
        this.toast.success('Project updated successfully');
      }
      
      return { success: true };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to update project: ${error.message}`);
      this.toast.error(`Failed to update project: ${error.message}`);
      throw error;
    }
  }

  async updateProjectField(projectId, field, value, showToast = false) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      
      await updateDoc(projectRef, {
        [field]: value,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      });
      
      this.logger.info('ProjectService', `Updated ${field} for project ${projectId}`);
      
      if (showToast) {
        this.toast.success(`${field} updated successfully`);
      }
      
      return { success: true };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to update ${field}: ${error.message}`);
      if (showToast) {
        this.toast.error(`Failed to update ${field}`);
      }
      throw error;
    }
  }

  async getProjectsByCompany(companyId) {
    try {
      const projectsRef = collection(db, 'projects');
      const q = query(projectsRef, where('companyId', '==', companyId));
      const querySnapshot = await getDocs(q);
      
      const projects = [];
      querySnapshot.forEach((doc) => {
        projects.push({ id: doc.id, ...doc.data() });
      });
      
      return projects;
    } catch (error) {
      this.logger.error('ProjectService', `Failed to get projects for company: ${error.message}`);
      throw error;
    }
  }

  async updateProjectBrief(projectId, briefData, showToast = true) {
    try {
      const projectRef = doc(db, 'projects', projectId);

      // Get current brief for comparison
      const currentDoc = await getDoc(projectRef);
      const currentData = currentDoc.data();
      const oldSections = currentData.breif?.sections || [];

      // Update the brief (stored as 'breif' - misspelled in database)
      const updates = {
        breif: { sections: briefData.sections },
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      };

      await updateDoc(projectRef, updates);

      // Only log activity on manual saves (showToast=true), not auto-saves
      // This prevents flooding the activity log with every keystroke
      if (showToast) {
        // Collect all changed fields for a single consolidated log entry
        const changedFields = [];

        for (let i = 0; i < briefData.sections.length; i++) {
          const newSection = briefData.sections[i];
          const oldSection = oldSections.find(s => s.name === newSection.name);

          // If section has fields array, compare each field
          if (newSection.fields && Array.isArray(newSection.fields)) {
            for (let j = 0; j < newSection.fields.length; j++) {
              const newField = newSection.fields[j];
              const oldField = oldSection?.fields?.find(f => f.input?.id === newField.input?.id);

              // Value is stored in field.input.value, not field.value
              const oldValue = oldField?.input?.value || '';
              const newValue = newField?.input?.value || '';

              // Only track if field value changed
              if (oldValue !== newValue) {
                const fieldLabel = newField.label || newField.input?.id;
                changedFields.push(fieldLabel);
              }
            }
          }
        }

        // Log a single activity entry summarizing the brief update
        if (changedFields.length > 0) {
          const fieldSummary = changedFields.length <= 3
            ? changedFields.join(', ')
            : `${changedFields.slice(0, 3).join(', ')} and ${changedFields.length - 3} more`;

          await this.logActivity(
            projectId,
            'brief_update',
            fieldSummary,
            null,
            `${changedFields.length} field(s) updated`,
            null
          );
        }
      }

      this.logger.info('ProjectService', `Project brief updated for ${projectId}`);

      if (showToast) {
        this.toast.success('Creative brief saved successfully');
      }

      return { success: true };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to update project brief: ${error.message}`);
      this.toast.error('Failed to update project brief');
      throw error;
    }
  }

  async updateProjectStatus(projectId, status, additionalData = {}, showToast = true) {
    try {
      const projectRef = doc(db, 'projects', projectId);

      // Get current status for logging
      const currentDoc = await getDoc(projectRef);
      const oldStatus = currentDoc.data()?.status;

      const updates = {
        status,
        ...additionalData,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      };

      if (status === PROJECT_STATUS.COMPLETED && !additionalData.completedDate) {
        updates.completedDate = Timestamp.now();
      }

      await updateDoc(projectRef, updates);

      // Log status change
      if (oldStatus && oldStatus !== status) {
        await this.logActivity(
          projectId,
          'status_change',
          'status',
          oldStatus,
          status,
          `Status changed from ${oldStatus} to ${status}`
        );
      }

      this.logger.info('ProjectService', `Project ${projectId} status updated to ${status}`);

      if (showToast) {
        this.toast.success(`Project status updated to ${status}`);
      }

      return { success: true };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to update project status: ${error.message}`);
      this.toast.error('Failed to update project status');
      throw error;
    }
  }

  async addProductionLog(projectId, logData) {
    try {
      const logsRef = collection(db, 'projects', projectId, 'productionLogs');
      
      const newLog = {
        ...logData,
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(logsRef, newLog);
      
      this.logger.info('ProjectService', `Production log added to project ${projectId}`);
      this.toast.success('Production log added successfully');
      
      return { success: true, id: docRef.id };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to add production log: ${error.message}`);
      this.toast.error('Failed to add production log');
      throw error;
    }
  }

  async getProductionLogs(projectId) {
    try {
      const logsRef = collection(db, 'projects', projectId, 'productionLogs');
      const querySnapshot = await getDocs(logsRef);
      
      const logs = [];
      querySnapshot.forEach((doc) => {
        logs.push({ id: doc.id, ...doc.data() });
      });
      
      return logs.sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis());
    } catch (error) {
      this.logger.error('ProjectService', `Failed to get production logs: ${error.message}`);
      throw error;
    }
  }

  async deleteProductionLog(projectId, logId) {
    try {
      const logRef = doc(db, 'projects', projectId, 'productionLogs', logId);
      await deleteDoc(logRef);
      
      this.logger.info('ProjectService', `Production log ${logId} deleted from project ${projectId}`);
      this.toast.success('Production log deleted');
      
      return { success: true };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to delete production log: ${error.message}`);
      this.toast.error('Failed to delete production log');
      throw error;
    }
  }

  async updateProductionSchedule(projectId, scheduleData, showToast = true) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      
      const updates = {
        productionSchedule: scheduleData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(projectRef, updates);
      
      this.logger.info('ProjectService', `Production schedule updated for project ${projectId}`);
      
      if (showToast) {
        this.toast.success('Production schedule updated successfully');
      }
      
      return { success: true };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to update production schedule: ${error.message}`);
      this.toast.error('Failed to update production schedule');
      throw error;
    }
  }

  async archiveProject(projectId) {
    try {
      const projectRef = doc(db, 'projects', projectId);

      await updateDoc(projectRef, {
        archived: true,
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Log archive activity
      await this.logActivity(
        projectId,
        'archived',
        'archived',
        false,
        true,
        'Project archived'
      );

      this.logger.info('ProjectService', `Project ${projectId} archived`);
      this.toast.success('Project archived successfully');

      return { success: true };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to archive project: ${error.message}`);
      this.toast.error('Failed to archive project');
      throw error;
    }
  }

  async unarchiveProject(projectId) {
    try {
      const projectRef = doc(db, 'projects', projectId);

      await updateDoc(projectRef, {
        archived: false,
        archivedAt: null,
        updatedAt: serverTimestamp()
      });

      // Log unarchive activity
      await this.logActivity(
        projectId,
        'unarchived',
        'archived',
        true,
        false,
        'Project restored from archive'
      );

      this.logger.info('ProjectService', `Project ${projectId} unarchived`);
      this.toast.success('Project restored successfully');

      return { success: true };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to unarchive project: ${error.message}`);
      this.toast.error('Failed to restore project');
      throw error;
    }
  }

  // Log activity for a project
  async logActivity(projectId, action, field, oldValue, newValue, note = null) {
    try {
      const user = auth.currentUser;
      const logsRef = collection(db, 'projects', projectId, 'activityLogs');

      // Determine user type from custom claims
      let userType = 'system';
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        const claims = idTokenResult.claims;

        if (claims.admin || claims.super) {
          userType = 'admin';
        } else if (claims.user) {
          userType = 'client';
        }
      }

      const logEntry = {
        projectId,
        action,
        field: field || null,
        oldValue: oldValue !== undefined ? oldValue : null,
        newValue: newValue !== undefined ? newValue : null,
        userId: user?.uid || 'system',
        userName: user?.displayName || user?.email || 'System',
        userType,
        timestamp: serverTimestamp(),
        note: note || null
      };

      await addDoc(logsRef, logEntry);
      this.logger.info('ProjectService', `Activity logged for project ${projectId}: ${action}`);
    } catch (error) {
      // Don't throw error for logging failures - we don't want to break the main operation
      this.logger.error('ProjectService', `Failed to log activity: ${error.message}`);
    }
  }

  // Get activity logs for a project
  async getProjectActivity(projectId, limit = 50) {
    try {
      const logsRef = collection(db, 'projects', projectId, 'activityLogs');
      const q = query(logsRef, orderBy('timestamp', 'desc'));

      const querySnapshot = await getDocs(q);
      const logs = [];

      querySnapshot.forEach((doc) => {
        if (logs.length < limit) {
          logs.push({ id: doc.id, ...doc.data() });
        }
      });

      return logs;
    } catch (error) {
      // If the query fails due to missing index, try without ordering
      if (error.code === 'failed-precondition') {
        // Log the full error message which should contain the index creation URL
        if (error.message && error.message.includes('https://console.firebase.google.com')) {
          this.logger.warn('ProjectService', `Firestore Index Required for Activity Logs: ${error.message}`);
        }

        const logsRef = collection(db, 'projects', projectId, 'activityLogs');
        const querySnapshot = await getDocs(logsRef);
        const logs = [];

        querySnapshot.forEach((doc) => {
          logs.push({ id: doc.id, ...doc.data() });
        });

        // Sort manually
        logs.sort((a, b) => {
          const timeA = a.timestamp?.toMillis ? a.timestamp.toMillis() : 0;
          const timeB = b.timestamp?.toMillis ? b.timestamp.toMillis() : 0;
          return timeB - timeA;
        });

        return logs.slice(0, limit);
      }

      this.logger.error('ProjectService', `Failed to get activity logs: ${error.message}`);
      return [];
    }
  }

  // Subscribe to activity logs for a project
  subscribeToProjectActivity(projectId, callback) {
    const logsRef = collection(db, 'projects', projectId, 'activityLogs');

    return onSnapshot(logsRef, (snapshot) => {
      const logs = [];

      snapshot.forEach((doc) => {
        logs.push({ id: doc.id, ...doc.data() });
      });

      // Sort by timestamp descending
      logs.sort((a, b) => {
        const timeA = a.timestamp?.toMillis ? a.timestamp.toMillis() : 0;
        const timeB = b.timestamp?.toMillis ? b.timestamp.toMillis() : 0;
        return timeB - timeA;
      });

      callback(logs);
    }, (error) => {
      this.logger.error('ProjectService', `Activity subscription error: ${error.message}`);
    });
  }

  /**
   * Submit client edit request - handles both new and accumulating changes
   * @param {string} projectId - Project ID
   * @param {object} currentBrief - Current brief data
   * @param {string} currentStatus - Current project status
   * @returns {Promise<{success: boolean}>}
   */
  async submitClientEditRequest(projectId, currentBrief, currentStatus) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const { briefChanges, originalBrief, currentSections, isFirstEdit } = await runTransaction(db, async (transaction) => {
        const projectSnap = await transaction.get(projectRef);

        if (!projectSnap.exists()) {
          throw new Error('Project not found');
        }

        const projectData = projectSnap.data();
        const existingPendingChanges = projectData.pendingChanges || {};
        const existingBriefChanges = existingPendingChanges.briefChanges || {};

        // Get current brief sections
        const currentSections = currentBrief?.sections || currentBrief || [];
        const originalBrief = projectData.breif?.sections || projectData.breif || [];

        // Build change tracking object - preserve ALL existing changes
        const briefChanges = { ...existingBriefChanges };
        const now = Date.now();

        this.logger.info('ProjectService', 'Starting change comparison', {
          existingChangesCount: Object.keys(existingBriefChanges).length,
          currentSectionsCount: currentSections.length
        });

        // Compare each field
        for (let i = 0; i < currentSections.length; i++) {
          const currentSection = currentSections[i];
          const originalSection = originalBrief.find(s => s.name === currentSection.name);

          if (currentSection.fields && Array.isArray(currentSection.fields)) {
            for (let j = 0; j < currentSection.fields.length; j++) {
              const currentField = currentSection.fields[j];
              const originalField = originalSection?.fields?.find(
                f => f.input?.id === currentField.input?.id
              );

              // Serialize values for proper comparison and storage
              const serializeValue = (val) => {
                if (val === null || val === undefined || val === '') return '';
                if (typeof val === 'object') return JSON.stringify(val);
                return String(val);
              };

              const currentValue = serializeValue(currentField.input?.value);
              const originalValue = serializeValue(originalField?.input?.value);
              const fieldId = currentField.input?.id;

              if (!fieldId) continue;

              // Check if this field already has pending changes
              if (existingBriefChanges[fieldId]) {
                // Keep the original oldValue, update currentValue
                const existingOldValue = existingBriefChanges[fieldId].oldValue;

                // If current value matches original, remove from pending
                if (currentValue === existingOldValue) {
                  delete briefChanges[fieldId];
                } else {
                  const changeData = {
                    oldValue: existingOldValue,
                    currentValue,
                    lastModified: now,
                    fieldLabel: currentField.label || fieldId,
                    sectionName: currentSection.name
                  };

                  // Add optional fields only if they exist (Firestore strips null values)
                  if (currentField.type) {
                    changeData.fieldType = currentField.type;
                  }
                  if (currentField.input?.options && currentField.input.options.length > 0) {
                    // Convert Proxy objects to plain objects for Firestore
                    changeData.fieldOptions = JSON.parse(JSON.stringify(currentField.input.options));
                  }

                  briefChanges[fieldId] = changeData;
                }
              } else if (currentValue !== originalValue) {
                // New change
                const changeData = {
                  oldValue: originalValue,
                  currentValue,
                  lastModified: now,
                  fieldLabel: currentField.label || fieldId,
                  sectionName: currentSection.name
                };

                // Debug: log ALL field data for troubleshooting
                this.logger.info('ProjectService', 'Processing field change', {
                  fieldLabel: currentField.label,
                  fieldType: currentField.type,
                  hasInputOptions: !!currentField.input?.options,
                  inputOptionsLength: currentField.input?.options?.length || 0,
                  inputOptionsFirstItem: currentField.input?.options?.[0]
                });

                // Add optional fields only if they exist (Firestore strips null values)
                if (currentField.type) {
                  changeData.fieldType = currentField.type;
                }
                if (currentField.input?.options && currentField.input.options.length > 0) {
                  // Convert Proxy objects to plain objects for Firestore
                  changeData.fieldOptions = JSON.parse(JSON.stringify(currentField.input.options));
                  this.logger.info('ProjectService', 'Stored fieldOptions (converted to plain objects)', {
                    fieldLabel: currentField.label,
                    optionsCount: changeData.fieldOptions.length,
                    firstOption: changeData.fieldOptions[0]
                  });
                }

                briefChanges[fieldId] = changeData;
              }
            }
          }
        }

        // Log final briefChanges
        this.logger.info('ProjectService', 'Final briefChanges', {
          totalChanges: Object.keys(briefChanges).length,
          changes: Object.entries(briefChanges).map(([id, change]) => ({
            id,
            label: change.fieldLabel,
            hasFieldType: !!change.fieldType,
            hasFieldOptions: !!change.fieldOptions
          }))
        });

        // Build pending changes object
        const isFirstEdit = !existingPendingChanges.firstRequestedAt;
        const pendingChanges = {
          // Keep quote/schedule changes from an earlier, still-unreviewed submit
          ...existingPendingChanges,
          briefChanges,
          firstRequestedAt: existingPendingChanges.firstRequestedAt || Timestamp.now(),
          lastModifiedAt: Timestamp.now(),
          requestedBy: auth.currentUser?.uid,
          previousStatus: existingPendingChanges.previousStatus || currentStatus,
          lastNotificationSent: existingPendingChanges.lastNotificationSent || null,
          // Store original brief structure to access field metadata (like radio options)
          originalBrief: existingPendingChanges.originalBrief || { sections: originalBrief }
        };

        const hasChanges =
          Object.keys(briefChanges).length +
            Object.keys(pendingChanges.quoteChanges || {}).length +
            (pendingChanges.scheduleChanges || []).length > 0;

        // Update project - IMPORTANT: Update the brief with new values so they display
        const updates = {
          breif: currentBrief, // Update the brief with the edited version
          // Every change reverted: back to where it was, not stuck In Review
          ...(hasChanges
            ? { pendingChanges, hasUnreviewedChanges: true, status: resolveSubmitStatus(projectData) }
            : { pendingChanges: null, hasUnreviewedChanges: false, status: pendingChanges.previousStatus }),
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid
        };

        this.logger.info('ProjectService', 'Updating project with changes', {
          briefSectionsCount: (currentBrief?.sections || currentBrief || []).length,
          pendingChangesCount: Object.keys(briefChanges).length
        });

        transaction.update(projectRef, updates);
        return { briefChanges, originalBrief, currentSections, isFirstEdit };
      });

      // Log activity
      const fieldCount = Object.keys(briefChanges).length;
      await this.logActivity(
        projectId,
        'client_edit_request',
        'brief',
        originalBrief,
        currentSections,
        `Client requested changes to ${fieldCount} field(s)`
      );

      this.logger.info('ProjectService', `Client edit request submitted for project ${projectId}`, {
        fieldCount,
        isFirstEdit
      });

      this.toast.success('Changes submitted for admin review');

      return { success: true };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to submit client edit request: ${error.message}`);
      this.toast.error('Failed to submit changes for review');
      throw error;
    }
  }

  /**
   * Submit a client edit to an MSA order's pricing quote. Mirrors
   * submitClientEditRequest: builds a human-readable diff of changed calculator
   * selections (preserving the original baseline across repeated edits),
   * writes the new quote, flips the project to In Review, and logs activity.
   *
   * @param {string} projectId
   * @param {Object} newQuote - { calculator, selections, lineItems, total, currency, computedAt }
   * @param {string} currentStatus - status before this edit
   * @returns {Promise<{success: boolean, changeCount: number}>}
   */
  async submitClientQuoteEditRequest(projectId, newQuote, currentStatus, scheduleChanges = []) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const { originalQuote, quoteChangeCount } = await runTransaction(db, async (transaction) => {
        const projectSnap = await transaction.get(projectRef);
        if (!projectSnap.exists()) {
          throw new Error('Project not found');
        }

        const projectData = projectSnap.data();
        const existingPending = projectData.pendingChanges || {};
        const existingQuoteChanges = existingPending.quoteChanges || {};
        const originalQuote = projectData.quote || {};
        const calculator = newQuote?.calculator || originalQuote.calculator || {};
        const blocks = calculator.blocks || [];
        const now = Date.now();

        // Humanize a selection value (choice ids -> option labels, arrays joined)
        const humanize = (block, value) => {
          if (block?.type === 'toggle') return value ? 'Yes' : 'No';
          if (value === null || value === undefined || value === '') return '—';
          if (block?.type === 'choice') {
            const labelFor = (id) =>
              (block.options || []).find((o) => o.id === id)?.label || String(id);
            return Array.isArray(value)
              ? value.map(labelFor).join(', ')
              : labelFor(value);
          }
          return String(value);
        };
        const serialize = (value) =>
          value === null || value === undefined
            ? ''
            : typeof value === 'object'
              ? JSON.stringify(value)
              : String(value);

        const oldSelections = originalQuote.selections || {};
        const newSelections = newQuote?.selections || {};
        const quoteChanges = { ...existingQuoteChanges };

        for (const block of blocks) {
          const key = block.key;
          if (!key) continue;
          const newRaw = newSelections[key];
          const newSerialized = serialize(newRaw);

          if (existingQuoteChanges[key]) {
            // Preserve the original baseline; drop the change if reverted
            const baseline = existingQuoteChanges[key].oldSerialized;
            if (newSerialized === baseline) {
              delete quoteChanges[key];
            } else {
              quoteChanges[key] = {
                ...existingQuoteChanges[key],
                currentValue: humanize(block, newRaw),
                currentSerialized: newSerialized,
                lastModified: now,
              };
            }
          } else {
            const oldRaw = oldSelections[key];
            const oldSerialized = serialize(oldRaw);
            if (newSerialized !== oldSerialized) {
              quoteChanges[key] = {
                fieldLabel: block.label || key,
                oldValue: humanize(block, oldRaw),
                oldSerialized,
                currentValue: humanize(block, newRaw),
                currentSerialized: newSerialized,
                lastModified: now,
              };
            }
          }
        }

        // Schedule changes (added/removed/moved production days) are already
        // applied to the calendar — they don't gate anything. We accumulate them
        // here purely as a review callout so the admin can see what shifted.
        const existingScheduleChanges = Array.isArray(existingPending.scheduleChanges)
          ? existingPending.scheduleChanges
          : [];
        const mergedScheduleChanges = [...existingScheduleChanges, ...(scheduleChanges || [])];

        const pendingChanges = {
          ...existingPending,
          quoteChanges,
          scheduleChanges: mergedScheduleChanges,
          firstRequestedAt: existingPending.firstRequestedAt || Timestamp.now(),
          lastModifiedAt: Timestamp.now(),
          requestedBy: auth.currentUser?.uid,
          previousStatus: existingPending.previousStatus || currentStatus,
        };

        const briefChangeCount = Object.keys(existingPending.briefChanges || {}).length;
        const quoteChangeCount = Object.keys(quoteChanges).length;
        const hasChanges =
          briefChangeCount + quoteChangeCount + mergedScheduleChanges.length > 0;

        transaction.update(projectRef, {
          quote: newQuote,
          // Price changed — the agency re-approves before it reaches us.
          // Every change reverted: back to where it was, not stuck In Review.
          ...(hasChanges
            ? { pendingChanges, hasUnreviewedChanges: true, status: resolveSubmitStatus(projectData) }
            : { pendingChanges: null, hasUnreviewedChanges: false, status: pendingChanges.previousStatus }),
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid,
        });
        return { originalQuote, quoteChangeCount };
      });

      await this.logActivity(
        projectId,
        'client_quote_edit_request',
        'quote',
        originalQuote,
        newQuote,
        `Client changed ${quoteChangeCount} order item(s)` +
          (scheduleChanges?.length ? ` and ${scheduleChanges.length} schedule item(s)` : '')
      );

      this.logger.info('ProjectService', `Client quote edit submitted for project ${projectId}`, {
        quoteChangeCount,
      });

      this.toast.success('Order changes submitted for review');
      return { success: true, changeCount: quoteChangeCount };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to submit quote edit: ${error.message}`);
      this.toast.error('Failed to submit order changes for review');
      throw error;
    }
  }

  /**
   * Apply an admin quote edit directly. Unlike submitClientQuoteEditRequest,
   * the admin is the authority: the new quote is written straight to the
   * project with no pendingChanges and no In Review status change.
   *
   * @param {string} projectId
   * @param {Object} newQuote - { calculator, selections, lineItems, total, currency, computedAt }
   * @returns {Promise<{success: boolean}>}
   */
  async updateProjectQuote(projectId, newQuote) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);
      if (!projectSnap.exists()) {
        throw new Error('Project not found');
      }

      const previousQuote = projectSnap.data().quote || {};

      await updateDoc(projectRef, {
        quote: newQuote,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid,
      });

      await this.logActivity(
        projectId,
        'quote_updated',
        'quote',
        previousQuote,
        newQuote,
        'Admin updated the order quote'
      );

      this.logger.info('ProjectService', `Admin updated quote for project ${projectId}`);
      this.toast.success('Order updated');
      return { success: true };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to update quote: ${error.message}`);
      this.toast.error('Failed to update the order');
      throw error;
    }
  }

  /**
   * Acknowledge client changes and restore project status
   * @param {string} projectId - Project ID
   * @returns {Promise<{success: boolean}>}
   */
  async acknowledgeClientChanges(projectId) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const { projectData } = await runTransaction(db, async (transaction) => {
        const projectSnap = await transaction.get(projectRef);

        if (!projectSnap.exists()) {
          throw new Error('Project not found');
        }

        const projectData = projectSnap.data();
        const previousStatus = projectData.pendingChanges?.previousStatus || PROJECT_STATUS.ACTIVE;

        // Clear pending changes and restore status
        const updates = {
          pendingChanges: null,
          hasUnreviewedChanges: false,
          status: previousStatus,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid
        };

        transaction.update(projectRef, updates);
        return { projectData };
      });

      // Log acknowledgment
      await this.logActivity(
        projectId,
        'client_changes_acknowledged',
        'pendingChanges',
        projectData.pendingChanges,
        null,
        'Admin acknowledged client changes'
      );

      this.logger.info('ProjectService', `Client changes acknowledged for project ${projectId}`);

      return { success: true };
    } catch (error) {
      this.logger.error('ProjectService', `Failed to acknowledge client changes: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get pending changes for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<object|null>}
   */
  async getPendingChanges(projectId) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);

      if (!projectSnap.exists()) {
        return null;
      }

      const projectData = projectSnap.data();
      return projectData.pendingChanges || null;
    } catch (error) {
      this.logger.error('ProjectService', `Failed to get pending changes: ${error.message}`);
      return null;
    }
  }
}

export const projectService = new ProjectService();